import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { type ColumnConfig, ColumnSelector } from './ColumnSelector';
import {
  DEFAULT_ACCOUNT_COLUMNS,
  DEFAULT_CONTACT_COLUMNS
} from './defaultColumns';
import { FilterBuilder } from './FilterBuilder';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/Dialog';
import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useFieldSelections } from '@/hooks/useFieldSelections';

const CREATE_SAVED_VIEW = gql`
  mutation CreateSavedView($values: SavedViewInput!) {
    createSavedView(values: $values) {
      ok
      error
      savedView {
        id
        name
      }
    }
  }
`;

const GET_DEPARTMENTS = gql`
  query GetDepartments {
    departments {
      id
      name
      completeName
    }
  }
`;

interface CreateViewDialogProps {
  onOpenChange: (open: boolean) => void;
  onViewCreated?: (id: string, targetModel: string) => void;
  open: boolean;
}

export function CreateViewDialog({
  onOpenChange,
  onViewCreated,
  open
}: CreateViewDialogProps) {
  const [name, setName] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [category, setCategory] = useState('');
  const [targetModel, setTargetModel] = useState('account');
  const [visibility, setVisibility] = useState('private');
  const [description, setDescription] = useState('');
  const [filterJson, setFilterJson] = useState('[]');
  const [selectedColumns, setSelectedColumns] = useState<ColumnConfig[]>(
    DEFAULT_ACCOUNT_COLUMNS
  );
  const [activeTab, setActiveTab] = useState('general');

  const [createView, { loading }] = useMutation(CREATE_SAVED_VIEW);
  const { data: departmentsData } = useQuery(GET_DEPARTMENTS);

  // Fetch field selections from backend for dropdowns
  const { options: targetModelOptions } = useFieldSelections(
    'opstream.saved.view',
    'target_model'
  );
  const { options: visibilityOptions } = useFieldSelections(
    'opstream.saved.view',
    'visibility'
  );

  const departments = departmentsData?.departments || [];

  // Update selected columns when target model changes
  useEffect(() => {
    setSelectedColumns(
      targetModel === 'account'
        ? DEFAULT_ACCOUNT_COLUMNS
        : DEFAULT_CONTACT_COLUMNS
    );
  }, [targetModel]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate JSON
    try {
      JSON.parse(filterJson);
    } catch (err) {
      toast.error(
        'Invalid Filter JSON: Please provide valid JSON for the filter criteria'
      );
      setActiveTab('filters');
      return;
    }

    // Serialize columns config
    const columnsConfig =
      selectedColumns.length > 0 ? JSON.stringify({ selectedColumns }) : null;

    const createValues = {
      category: category || null,
      columnsConfig,
      departmentId: departmentId || null,
      description: description || null,
      filterJson,
      name,
      targetModel,
      visibility
    };

    try {
      const { data } = await createView({
        variables: {
          values: createValues
        }
      });

      if (data?.createSavedView?.ok) {
        const createdId = data.createSavedView.savedView?.id;
        toast.success(`Successfully created view "${name}"`);

        // Call callback with id and targetModel if provided
        if (createdId && onViewCreated) {
          onViewCreated(createdId, targetModel);
        }

        // Close dialog
        onOpenChange(false);

        // Reset form
        setName('');
        setDepartmentId('');
        setCategory('');
        setTargetModel('account');
        setVisibility('private');
        setDescription('');
        setFilterJson('[]');
        setSelectedColumns(DEFAULT_ACCOUNT_COLUMNS);
        setActiveTab('general');
      } else {
        toast.error(data?.createSavedView?.error || 'Failed to create view');
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    }
  };

  return (
    <Dialog
      onOpenChange={onOpenChange}
      open={open}
    >
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Create Saved View</DialogTitle>
          <DialogDescription>
            Create a new saved view with custom filters for accounts or contacts
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <form onSubmit={handleSubmit}>
            <Tabs
              className="space-y-4"
              onValueChange={setActiveTab}
              value={activeTab}
            >
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="general">General</TabsTrigger>
                <TabsTrigger value="filters">Filters</TabsTrigger>
                <TabsTrigger value="columns">Columns</TabsTrigger>
              </TabsList>

              <TabsContent
                className="mt-0 space-y-4"
                value="general"
              >
                <Field className="gap-2">
                  <FieldLabel htmlFor="name">View Name *</FieldLabel>
                  <Input
                    id="name"
                    onChange={e => {
                      setName(e.target.value);
                    }}
                    placeholder="e.g., High Priority Investors"
                    required
                    value={name}
                  />
                </Field>

                <div className="grid grid-cols-2 gap-4">
                  <Field className="gap-2">
                    <FieldLabel htmlFor="department">Department</FieldLabel>
                    <Select
                      onValueChange={value => {
                        setDepartmentId(value === '__none__' ? '' : value);
                      }}
                      value={departmentId || '__none__'}
                    >
                      <SelectTrigger id="department">
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">No Department</SelectItem>
                        {departments.map((dept: any) => (
                          <SelectItem
                            key={dept.id}
                            value={dept.id}
                          >
                            {dept.completeName || dept.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field className="gap-2">
                    <FieldLabel htmlFor="category">Category</FieldLabel>
                    <Input
                      id="category"
                      onChange={e => {
                        setCategory(e.target.value);
                      }}
                      placeholder="e.g., Fundraising, Investor Relations"
                      value={category}
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Field className="gap-2">
                    <FieldLabel htmlFor="targetModel">
                      Target Model *
                    </FieldLabel>
                    <Select
                      onValueChange={setTargetModel}
                      value={targetModel}
                    >
                      <SelectTrigger id="targetModel">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {targetModelOptions.map(option => (
                          <SelectItem
                            key={option.value}
                            value={option.value}
                          >
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field className="gap-2">
                    <FieldLabel htmlFor="visibility">Visibility *</FieldLabel>
                    <Select
                      onValueChange={setVisibility}
                      value={visibility}
                    >
                      <SelectTrigger id="visibility">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {visibilityOptions.map(option => (
                          <SelectItem
                            key={option.value}
                            value={option.value}
                          >
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <Field className="gap-2">
                  <FieldLabel htmlFor="description">Description</FieldLabel>
                  <Textarea
                    id="description"
                    onChange={e => {
                      setDescription(e.target.value);
                    }}
                    placeholder="Describe what this view contains..."
                    rows={3}
                    value={description}
                  />
                </Field>
              </TabsContent>

              <TabsContent
                className="mt-0"
                value="filters"
              >
                <FilterBuilder
                  onChange={setFilterJson}
                  targetModel={targetModel as 'account' | 'contact'}
                  value={filterJson}
                />
              </TabsContent>

              <TabsContent
                className="mt-0"
                value="columns"
              >
                <ColumnSelector
                  onChange={setSelectedColumns}
                  selectedColumns={selectedColumns}
                  targetModel={targetModel as 'account' | 'contact'}
                />
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6">
              <Button
                onClick={() => {
                  onOpenChange(false);
                }}
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                disabled={loading}
                type="submit"
              >
                {loading ? 'Creating...' : 'Create View'}
              </Button>
            </DialogFooter>
          </form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
